/**
 * 供給力変化画面 — mock データ定義 (将来 BFF message 形に寄せた型)
 *
 * TODO(#1692): 実データ配管 (proto/bff/backend) — 現状は計画停止 mock
 * TODO(#1691): 定期点検データの取得元・更新頻度は PdM 確認待ち
 *
 * 日本語の company/plant/unit/fuel/area 名は API 由来データ扱いのため i18n 対象外。
 * 受渡日 (tmr) に対して today = delivery − 1 day とする。
 */
import { LightModeSemanticColors } from '@enechain/ecloud-designsystem'

import { addDays } from '~/models/dateTime'

/** 燃料種別 */
export type FuelType =
  | 'Nuclear'
  | 'Hydro'
  | 'ThermalCoal'
  | 'ThermalGas'
  | 'ThermalOil'
  | 'Geothermal'
  | 'Wind'
  | 'Solar'
  | 'Other'

/** 計画停止アイテム (将来 BFF message: SupplyCapacityChangeItem) */
export type SupplyCapacityItem = {
  area: string
  company: string
  plant: string
  fuel: FuelType
  unit: string
  capacityKw: number
  /** start date (local) */
  start: Date
  /** end date (local) */
  end: Date
}

/** 燃料種別の日本語ラベル */
export const FUEL_LABELS: Record<FuelType, string> = {
  Nuclear: '原子力',
  Hydro: '水力',
  ThermalCoal: '石炭火力',
  ThermalGas: 'ガス火力',
  ThermalOil: '石油火力',
  Geothermal: '地熱',
  Wind: '風力',
  Solar: '太陽光',
  Other: 'その他',
}

/** エリア順序 */
export const AREA_ORDER = [
  '北海道',
  '東北',
  '東京',
  '中部',
  '北陸',
  '関西',
  '中国',
  '四国',
  '九州',
] as const

/**
 * エリアカラー (chart bar fill / area header net MW color)
 * design JS AREA_COLORS 準拠。
 */
export const AREA_COLORS: Record<string, string> = {
  // --brand-purple => #bb97ff
  北海道: LightModeSemanticColors.Brand.purple.value,
  // --brand-blue => #0c00c5
  東北: LightModeSemanticColors.Brand.blue.value,
  // --surface-accent-primary => #0c00c5
  東京: LightModeSemanticColors.Surface.accentPrimary.value,
  // --text-warning-high => #5d4e00
  中部: LightModeSemanticColors.Text.warningHigh.value,
  // --brand-green => #69d582
  北陸: LightModeSemanticColors.Brand.green.value,
  // --brand-red => #ff1e23
  関西: LightModeSemanticColors.Brand.red.value,
  // --brand-pink => #ff6e6e
  中国: LightModeSemanticColors.Brand.pink.value,
  // --brand-teal => #00b0b2
  四国: LightModeSemanticColors.Brand.teal.value,
  // --brand-yellow => #ffdd29
  九州: LightModeSemanticColors.Brand.yellow.value,
}

/**
 * mock データを today / tmr から生成する。
 * 受渡日 = tmr、 約定日 = today (= tmr − 1)。
 * design lines 314-332 の 11 件を移植。
 */
export const getMockItems = (today: Date, tmr: Date): SupplyCapacityItem[] => [
  // 当日 作業開始
  {
    area: '東京',
    company: 'JERA',
    plant: '姉崎火力発電所',
    fuel: 'ThermalGas',
    unit: '5号機',
    capacityKw: 600000,
    start: today,
    end: addDays(today, 14),
  },
  {
    area: '東京',
    company: 'JERA',
    plant: '袖ヶ浦火力発電所',
    fuel: 'ThermalGas',
    unit: '1号機',
    capacityKw: 1000000,
    start: today,
    end: addDays(today, 21),
  },
  {
    area: '関西',
    company: '関西電力',
    plant: '高浜発電所',
    fuel: 'Nuclear',
    unit: '3号機',
    capacityKw: 870000,
    start: today,
    end: addDays(today, 90),
  },
  // 当日 作業終了
  {
    area: '東京',
    company: 'JERA',
    plant: '富津火力発電所',
    fuel: 'ThermalGas',
    unit: '4-1号機',
    capacityKw: 507000,
    start: addDays(today, -30),
    end: today,
  },
  {
    area: '中部',
    company: 'JERA',
    plant: '碧南火力発電所',
    fuel: 'ThermalCoal',
    unit: '3号機',
    capacityKw: 700000,
    start: addDays(today, -14),
    end: today,
  },
  // 翌日 作業開始
  {
    area: '北海道',
    company: '北海道電力',
    plant: '苫東厚真発電所',
    fuel: 'ThermalCoal',
    unit: '4号機',
    capacityKw: 700000,
    start: tmr,
    end: addDays(tmr, 10),
  },
  {
    area: '九州',
    company: '九州電力',
    plant: '新大分発電所',
    fuel: 'ThermalGas',
    unit: '3-1号機',
    capacityKw: 245000,
    start: tmr,
    end: addDays(tmr, 7),
  },
  {
    area: '東北',
    company: '東北電力',
    plant: '能代火力発電所',
    fuel: 'ThermalCoal',
    unit: '1号機',
    capacityKw: 600000,
    start: tmr,
    end: addDays(tmr, 28),
  },
  // 翌日 作業終了
  {
    area: '関西',
    company: '関西電力',
    plant: '大飯発電所',
    fuel: 'Nuclear',
    unit: '4号機',
    capacityKw: 1180000,
    start: addDays(tmr, -60),
    end: tmr,
  },
  {
    area: '四国',
    company: '四国電力',
    plant: '橘湾発電所',
    fuel: 'ThermalCoal',
    unit: '1号機',
    capacityKw: 700000,
    start: addDays(tmr, -7),
    end: tmr,
  },
  {
    area: '中国',
    company: '中国電力',
    plant: '三隅発電所',
    fuel: 'ThermalCoal',
    unit: '2号機',
    capacityKw: 1000000,
    start: addDays(tmr, -21),
    end: tmr,
  },
]
